import { PropertyDocExportDialogComponent } from '@/components/property-doc-export-dialog/property-doc-export-dialog.component';
import { VaultDocTableComponent } from '@/components/vault-doc-table/vault-doc-table.component';
import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AppConfigService } from '@/services/app-config.service';
import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelect } from '@angular/material/select';
import {
  DocumentCategory,
  PropertiesService,
  RealProperty,
  SharingDataCategory,
  SharingScope,
  VaultService
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { VaultDocTableComponent as VaultDocTableComponent_1 } from '../../components/vault-doc-table/vault-doc-table.component';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { InputTrimDirective } from '../../extensions/inputTrimDirective';

export interface VaultCategoryData {
  category: DocumentCategory;
  visible: boolean;
  name: string;
  description: string;
  expanded: boolean;
  docCount: number;
}

@Component({
  selector: 'app-vault',
  templateUrl: './vault.component.html',
  styleUrls: ['./vault.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    InputTrimDirective,
    MatButtonModule,
    MatExpansionModule,
    NgFor,
    VaultDocTableComponent_1,
    TooltipDirective
  ]
})
export class VaultComponent implements OnInit, OnDestroy {
  @ViewChild('propSelect', { static: true }) propSelect: MatSelect;
  @ViewChildren(VaultDocTableComponent) dataComponents: QueryList<VaultDocTableComponent>;

  subscriptions: Subscription[] = [];

  props: RealProperty[];
  prop: RealProperty;
  canShowDownload = false;
  downloadDisabled = true;
  docCategories: VaultCategoryData[] = [];
  noCategoryVisible = false;

  constructor(
    private propertyService: PropertiesService,
    private vaultService: VaultService,
    public propHelper: PropertyHelper,
    public userHelper: UserHelper,
    private dialog: MatDialog,
    private appConfig: AppConfigService,
    private appConfigService: AppConfigService
  ) {}

  ngOnInit() {
    this.prop = this.appConfig.selectedProp;
    this.propHelper.getDocumentCategories().forEach((c) =>
      this.docCategories.push({
        category: c,
        visible: this.userHelper.canViewCategory(this.prop, c as SharingDataCategory),
        name: this.propHelper.getDocumentCategoryName(c, this.prop.type),
        description: '',
        expanded: false,
        docCount: 0
      })
    );
    this.noCategoryVisible = this.docCategories.filter((c) => c.visible).length === 0;
    this.canShowDownload = this.userHelper.canDownloadDocProperty();
    this.detectPanelToExpand();
    this.subscriptions.push(
      this.appConfigService.objectUpdated.subscribe((type) => {
        if (type === ObjectUpdatedType.DOC) {
          this.refreshDocList();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  refreshDocList() {
    this.detectPanelToExpand();
    this.dataComponents.forEach((comp) => {
      comp.prop = this.prop;
      comp.loadData();
    });
  }

  getDocCategoryData(category: DocumentCategory): VaultCategoryData {
    return this.docCategories.find((d) => d.category === category);
  }

  applyGlobalFilter(filter: string) {
    this.dataComponents.forEach((comp) => {
      comp.applyFilter(filter);
    });
    this.dataComponents.forEach((comp) => {
      const docData = this.getDocCategoryData(comp.category);
      docData.docCount = comp.dataSource.filteredData.length;
      docData.expanded = docData.docCount > 0;
      docData.description = this.getPanelDescription(comp.category);
    });
  }

  detectPanelToExpand() {
    // TODO we need to do a request to know if there is a document in a category.
    // The same request is made again when the doc table loads => need to be optimized !!!
    if (this.prop) {
      this.propHelper.getDocumentCategories().forEach((categ) => {
        if (this.getDocCategoryData(categ).visible) {
          this.subscriptions.push(
            this.vaultService.getRealPropertyDocuments(this.prop.uid, DocumentCategory[categ]).subscribe((data) => {
              const docData = this.getDocCategoryData(categ);
              docData.expanded = data.length > 0;
              docData.docCount = data.length;
              this.downloadDisabled = !this.downloadDisabled ? false : data.length === 0;
              docData.description = this.getPanelDescription(categ);
              // if the sharing scope is viewing and no document -> hide the panel
              if (docData.docCount == 0 && SharingScope.VIEWING === this.prop.details.accessSharing?.scope) {
                docData.visible = false;
              }
            })
          );
        }
      });
    }
  }

  downloadDocuments() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const data: any = { property: this.prop };
      const dialogRef = this.dialog.open(PropertyDocExportDialogComponent, {
        data
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
        }
      });
    }
  }

  getPanelDescription(categ: DocumentCategory) {
    const data = this.getDocCategoryData(categ);
    if (!data.docCount) {
      return 'Aucun document';
    }
    switch (data.docCount) {
      case 0:
        return 'Aucun document';
      case 1:
        return '1 document';
      default:
        return data.docCount + ' documents';
    }
  }
}
